import {
  Asset,
  BrandSpaceStaffView,
  IssuerAssetListItem,
  IssuerAssetStatusOverride,
  IssuerAssetsListResponse,
  IssuerKey,
  IssuerSummary,
  IssuerSyncTriggerResponse,
  MessageResponse,
  SetIssuerAssetStatusRequest,
  UpdateBrandSpaceRequest,
} from '@mygate/common';
import { api } from './api';

export interface IssuerAssetsParams {
  linked?: boolean;
  search?: string;
  includeInactive?: boolean;
}

/**
 * Issuers = the read-only content feeds behind RWA assets (Lympid, …). Mirrors are synced nightly;
 * this client only reads them, triggers a sync, and manages the asset ↔ issuer-record links.
 */
class IssuersService {
  async listIssuers() {
    return api.get<IssuerSummary[]>('/api/staff/issuers');
  }

  async triggerSync(issuer: IssuerKey) {
    return api.post<IssuerSyncTriggerResponse>(`/api/staff/issuers/${issuer}/sync`, {});
  }

  async listIssuerAssets(issuer: IssuerKey, params: IssuerAssetsParams = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') qs.append(key, String(value));
    });
    const queryString = qs.toString();
    return api.get<IssuerAssetsListResponse>(
      `/api/staff/issuers/${issuer}/assets${queryString ? `?${queryString}` : ''}`,
    );
  }

  async getIssuerAsset(issuer: IssuerKey, issuerAssetId: string) {
    return api.get<IssuerAssetListItem>(`/api/staff/issuers/${issuer}/assets/${issuerAssetId}`);
  }

  async linkAsset(issuer: IssuerKey, issuerAssetId: string, assetId: string) {
    return api.post<Asset>(`/api/staff/issuers/${issuer}/assets/${issuerAssetId}/link`, { assetId });
  }

  /**
   * Declare the tranche's lifecycle, or `null` to hand it back to the subscription window.
   *
   * It is not a backoffice label: the same value decides the badge in the app and whether a
   * purchase is accepted at all (MYG-897).
   */
  async setAssetStatus(
    issuer: IssuerKey,
    issuerAssetId: string,
    status: IssuerAssetStatusOverride | null,
    // When the declaration ends and the dates decide again, ISO 8601 (MYG-898). Only with a status.
    until: string | null = null,
  ) {
    const body: SetIssuerAssetStatusRequest = { status, until };
    return api.put<IssuerAssetListItem>(`/api/staff/issuers/${issuer}/assets/${issuerAssetId}/status`, body);
  }

  /** The issuer's brand space with its readiness checklist (MYG-732). */
  async getBrandSpace(issuer: IssuerKey) {
    return api.get<BrandSpaceStaffView>(`/api/staff/issuers/${issuer}/brand-space`);
  }

  async updateBrandSpace(issuer: IssuerKey, data: UpdateBrandSpaceRequest) {
    return api.put<BrandSpaceStaffView>(`/api/staff/issuers/${issuer}/brand-space`, data);
  }

  async unlinkAsset(issuer: IssuerKey, issuerAssetId: string) {
    return api.delete<MessageResponse>(`/api/staff/issuers/${issuer}/assets/${issuerAssetId}/link`);
  }
}

export const issuersService = new IssuersService();
